import { supabase } from '@/lib/supabase';
import { NUTRIENT_CONFIG, type NutrientKey } from '@/hooks/useMicronutrients';

/**
 * Разбор ОДНОГО приёма по нутриентам — целиком из базы, без вызова модели.
 *
 * ЗАЧЕМ (решение Славы 29.09.2026). «Узнать пользу продуктов» и раньше работала
 * на весь приём, но текст к КАЖДОМУ нутриенту писала модель — поэтому чем богаче
 * приём, тем длиннее портянка. Слава: «не должно быть длинной портянки».
 *
 * 🔑 Портянка лечится структурно, а не просьбой «пиши короче»: числа берём
 * из `meal_items` (их уже досчитал `enrich-meal`), а у модели остаётся максимум
 * одна фраза. Длина разбора перестаёт расти вместе с числом нутриентов.
 *
 * Побочно это быстро и бесплатно: раньше открытие стоило 0.3–0.6 ₽ и секунды
 * ожидания, теперь блоки появляются сразу.
 *
 * ⚠️ Обогащены не все позиции (на 29.09.2026 — 6406 из 8098, 79%). Если в приёме
 * есть недосчитанные, отдаём `pending: true`, и карточка честно говорит
 * «досчитываю», а не рисует заниженные цифры как факт.
 */

export type BreakRow = {
  key: NutrientKey;
  label: string;
  emoji: string;
  amount: number;
  unit: string;
  percent: number;
  /** Зачем это телу — одной строкой, без медицинских утверждений */
  benefit: string;
};

export type MealBreakdown = {
  vitamins: BreakRow[];
  minerals: BreakRow[];
  basics: BreakRow[];
  /** Топ по проценту нормы — то, что видно сразу, без раскрытия блоков */
  chips: BreakRow[];
  /** Главный нутриент приёма: по нему подбирается заметка исследования */
  lead: NutrientKey | null;
  pending: boolean;
  empty: boolean;
};

/** Колонка `meal_items` для каждого нутриента */
const COLUMN: Record<string, string> = {
  fiber: 'fiber_g',
  vegetables_fruits: 'vegetables_fruits_g',
  omega3: 'omega3_g',
  calcium: 'calcium_mg',
  iron: 'iron_mg',
  magnesium: 'magnesium_mg',
  zinc: 'zinc_mg',
  potassium: 'potassium_mg',
  vitamin_d: 'vitamin_d_mcg',
  vitamin_b12: 'vitamin_b12_mcg',
  vitamin_c: 'vitamin_c_mg',
};

const UNIT: Record<string, string> = {
  fiber: 'г', vegetables_fruits: 'г', omega3: 'г',
  calcium: 'мг', iron: 'мг', magnesium: 'мг', zinc: 'мг', potassium: 'мг',
  vitamin_d: 'мкг', vitamin_b12: 'мкг', vitamin_c: 'мг',
};

/**
 * Зачем нутриент телу — одна строка на человеческом языке.
 *
 * ⚠️ Регистр формулировок (требование Славы 29.09.2026): «участвует»,
 * «поддерживает», «нужен» — и никогда «лечит», «защищает от болезни».
 * Всё, что выходит за питание, адресуется лечащему врачу, а не приложению.
 */
const BENEFIT: Record<string, string> = {
  fiber: 'кормит микробиом и дольше держит сытость',
  vegetables_fruits: 'основа рациона: по рекомендациям ВОЗ — от 400 г в день',
  omega3: 'поддерживает сердце и сосуды',
  calcium: 'нужен костям, зубам и работе мышц',
  iron: 'переносит кислород — отсюда энергия и выносливость',
  magnesium: 'участвует в работе мышц и нервной системы',
  zinc: 'нужен иммунитету, коже и заживлению',
  potassium: 'помогает поддерживать нормальное давление',
  vitamin_d: 'работает вместе с кальцием для костей и мышц',
  vitamin_b12: 'нужен нервной системе и кроветворению',
  vitamin_c: 'участвует в иммунной защите и помогает усваивать железо из растений',
};

const VITAMINS: NutrientKey[] = ['vitamin_d', 'vitamin_b12', 'vitamin_c'];
const MINERALS: NutrientKey[] = ['calcium', 'iron', 'magnesium', 'zinc', 'potassium'];
const BASICS: NutrientKey[] = ['fiber', 'vegetables_fruits', 'omega3'];

/** Ниже этого процента нутриент не показываем чипом: это шум, а не вклад */
const CHIP_MIN_PERCENT = 15;
const MAX_CHIPS = 3;

function row(key: NutrientKey, amount: number, target: number): BreakRow | null {
  if (!(target > 0) || !(amount > 0)) return null;
  const cfg = NUTRIENT_CONFIG[key];
  if (!cfg) return null;
  return {
    key,
    label: cfg.label,
    emoji: cfg.emoji,
    amount: amount >= 10 ? Math.round(amount) : Math.round(amount * 10) / 10,
    unit: UNIT[key] ?? '',
    percent: Math.round((amount / target) * 100),
    benefit: BENEFIT[key] ?? '',
  };
}

export async function getMealBreakdown(mealId: string): Promise<MealBreakdown> {
  const empty: MealBreakdown = {
    vitamins: [], minerals: [], basics: [], chips: [],
    lead: null, pending: false, empty: true,
  };

  try {
    const cols = [...Object.values(COLUMN), 'enriched_at'];
    const [{ data: items }, { data: summary }] = await Promise.all([
      supabase.from('meal_items').select(cols.join(',')).eq('meal_id', mealId),
      // Нормы зависят от пола, возраста, веса и калорийности человека —
      // берём их из того же источника, что и экран микронутриентов.
      supabase.rpc('get_daily_nutrients_summary', {
        p_date: new Date().toISOString().slice(0, 10),
      }),
    ]);

    if (!items?.length || !summary) return empty;

    const targets = (summary as any).targets ?? {};
    const pending = items.some((it: any) => !it.enriched_at);

    const build = (keys: NutrientKey[]) =>
      keys
        .map((key) => {
          const sum = items.reduce(
            (acc: number, it: any) => acc + Number(it[COLUMN[key]] || 0), 0,
          );
          return row(key, sum, Number(targets[key]?.amount || 0));
        })
        .filter(Boolean) as BreakRow[];

    const vitamins = build(VITAMINS);
    const minerals = build(MINERALS);
    const basics = build(BASICS);

    const chips = [...basics, ...minerals, ...vitamins]
      .filter((r) => r.percent >= CHIP_MIN_PERCENT)
      .sort((a, b) => b.percent - a.percent)
      .slice(0, MAX_CHIPS);

    return {
      vitamins, minerals, basics, chips,
      lead: chips[0]?.key ?? null,
      pending,
      empty: !vitamins.length && !minerals.length && !basics.length,
    };
  } catch (e) {
    console.error('meal breakdown error', e);
    return empty;
  }
}

/**
 * Фраза-вердикт над блоками. Считается на клиенте: одна строка про то,
 * чем приём силён, без пересказа БЖУ (их человек уже видит выше — урок 05.09,
 * когда строку-вывод пришлось убирать именно за повторение).
 */
export function breakdownVerdict(b: MealBreakdown): string | null {
  if (b.empty) return null;
  const top = b.chips[0];
  if (!top) return 'Заметного вклада по витаминам и минералам в этом приёме немного — это нормально, норма набирается за день целиком.';
  const rest = b.chips.slice(1).map((r) => r.label.toLowerCase());
  const tail = rest.length ? `, рядом ${rest.join(' и ')}` : '';
  return `Сильная сторона приёма — ${top.label.toLowerCase()}: около ${top.percent}% дневной нормы${tail}.`;
}
